import React, { useEffect, useState } from 'react';
import { useAuth } from '../../auth/firebaseAuth';
import { remoteConfig } from '../../utility/firebaseRemoteConfigHelper';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { Tenancy } from '../../types/tenancyTypes';
import { ManagedProperty } from '../property/property-card/PropertyCardTypes';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  HomeIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { Timestamp } from 'firebase/firestore';

interface ComplianceDashboardProps {
  className?: string;
}

interface ComplianceRule {
  category: string;
  description: string;
  rtbRegistration?: {
    required: boolean;
    frequency: string;
    deadline: string;
    link: string;
  };
  terminationGrounds?: Array<{
    ground: string;
    noticePeriod: number;
    requiresRtb: boolean;
  }>;
  rentReview?: {
    eligible: boolean;
    minPeriod: number;
    maxIncrease: string;
    noticeRequired: number;
  };
  depositProtection?: {
    required: boolean;
    scheme: string;
    interestReturn: boolean;
  };
  inspectionRights?: {
    allowed: boolean;
    frequency: string;
    unreasonableDenial: string;
  };
  additionalComplianceNotes?: string[];
}

const ComplianceDashboard: React.FC<ComplianceDashboardProps> = ({
  className = ''
}) => {
  const { currentUser, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState<ManagedProperty[]>([]);
  const [tenancies, setTenancies] = useState<{
    [propertyId: number]: Tenancy[];
  }>({});
  const [rpzCap, setRpzCap] = useState(2);
  const [reviewPeriod, setReviewPeriod] = useState(24);
  const [tenancyCount, setTenancyCount] = useState<number | null>(null);
  const [editingTenancyCount, setEditingTenancyCount] = useState(false);
  const [tempTenancyCount, setTempTenancyCount] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [complianceRules, setComplianceRules] = useState<ComplianceRule | null>(
    null
  );
  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({
    rtbRegistration: true,
    terminationGrounds: true,
    rentReview: false,
    depositProtection: false,
    inspectionRights: false,
    additionalNotes: false
  });
  const [modal, setModal] = useState<{
    isOpen: boolean;
    type: 'error' | 'warning' | 'success' | 'info';
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: ''
  });

  useEffect(() => {
    if (userProfile) {
      const count = (userProfile as any).tenancyCount || null;
      setTenancyCount(count);
    }
  }, [userProfile]);

  useEffect(() => {
    // Fetch compliance rules based on tenancy count
    if (tenancyCount !== null) {
      try {
        const isSmall = tenancyCount <= 3;
        const rulesJson = isSmall
          ? remoteConfig.getSmallLandlordRules()
          : remoteConfig.getLargeLandlordRules();

        if (rulesJson) {
          const rules =
            typeof rulesJson === 'string' ? JSON.parse(rulesJson) : rulesJson;
          setComplianceRules(rules);
        }
      } catch (error) {
        console.error('Error fetching compliance rules:', error);
      }
    }
  }, [tenancyCount]);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) return;
      setLoading(true);
      try {
        const token = await currentUser.getIdToken();

        // 1. Fetch Properties
        const propsResponse = await fetch(
          `${CLOUD_FUNCTIONS_BASE_URL}/getManagedProperties`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        const propsData = await propsResponse.json();
        const props = propsData.data || [];
        setProperties(props);

        // 2. Fetch Tenancies for each property
        const tenanciesMap: { [key: number]: Tenancy[] } = {};

        await Promise.all(
          props.map(async (p: ManagedProperty) => {
            try {
              const tenResponse = await fetch(
                `${CLOUD_FUNCTIONS_BASE_URL}/getTenantsForProperty?listingId=${p.id}`,
                {
                  headers: { Authorization: `Bearer ${token}` }
                }
              );
              if (tenResponse.ok) {
                const tenData = await tenResponse.json();
                tenanciesMap[p.id] = tenData.data || [];
              }
            } catch (e) {
              console.error(`Failed to fetch tenants for property ${p.id}`, e);
            }
          })
        );

        setTenancies(tenanciesMap);
      } catch (error) {
        console.error('Error fetching compliance data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  const isSmallLandlord = tenancyCount !== null && tenancyCount <= 3;
  const isLargeLandlord = tenancyCount !== null && tenancyCount >= 4;

  const handleSaveTenancyCount = async () => {
    if (!currentUser) return;

    const count = parseInt(tempTenancyCount, 10);
    if (isNaN(count) || count < 0) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Invalid Input',
        message: 'Please enter a valid number'
      });
      return;
    }

    setSaving(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${CLOUD_FUNCTIONS_BASE_URL}/updateUserTenancyCount`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ tenancyCount: count })
        }
      );

      if (!response.ok) throw new Error('Failed to update');

      setTenancyCount(count);
      setEditingTenancyCount(false);
    } catch (error) {
      console.error('Error saving tenancy count:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Save Failed',
        message: 'Failed to save tenancy count. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  const parseTimestamp = (val: any): Date => {
    if (!val) return new Date();
    if (val instanceof Timestamp) return val.toDate();
    if (typeof val === 'object' && '_seconds' in val) {
      return new Date(val._seconds * 1000);
    }
    return new Date(val);
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getComplianceStatus = (
    property: ManagedProperty,
    propertyTenancies: Tenancy[]
  ) => {
    const activeTenancy = propertyTenancies.find(
      (t) =>
        !t.leaseEndDate || parseTimestamp(t.leaseEndDate).getTime() > Date.now()
    );

    if (!activeTenancy) return null;

    const now = new Date();
    const issues = [];
    const checks = [];

    // Check RTB Registration
    if (!activeTenancy.rtbRegisteredOn) {
      issues.push({
        type: 'warning',
        message:
          'RTB Registration date not recorded for a tenancy in this property',
        action: 'View Tenancies & Edit',
        url: `/property/${property.id}/tenants`
      });
    } else {
      const regDate = parseTimestamp(activeTenancy.rtbRegisteredOn);

      const nextDueDate = new Date(regDate);
      nextDueDate.setFullYear(nextDueDate.getFullYear() + 1);

      const daysUntilDue = Math.ceil(
        (nextDueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (daysUntilDue < 0) {
        issues.push({
          type: 'error',
          message: `RTB Registration overdue by ${Math.abs(daysUntilDue)} days`,
          action: 'Register Now',
          url: 'https://portal.rtb.ie/portal-login'
        });
      } else if (daysUntilDue <= 30) {
        issues.push({
          type: 'warning',
          message: `RTB Registration due in ${daysUntilDue} days`,
          action: 'Prepare Renewal',
          url: 'https://portal.rtb.ie/portal-login'
        });
      } else {
        checks.push(
          `RTB Registration completed (Next due: ${nextDueDate.toLocaleDateString(
            'en-IE'
          )})`
        );
      }
    }

    // Check Rent Review
    const leaseStart = parseTimestamp(activeTenancy.leaseStartDate);

    const monthsSinceStart =
      (now.getFullYear() - leaseStart.getFullYear()) * 12 +
      (now.getMonth() - leaseStart.getMonth());

    if (monthsSinceStart >= reviewPeriod) {
      issues.push({
        type: 'info',
        message: `Eligible for Rent Review (>${reviewPeriod} months)`,
        detail: `Max increase: ${rpzCap}% (RPZ Cap)`,
        action: 'Review Rent'
      });
    }

    return { activeTenancy, issues, checks };
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">
        Loading compliance data...
      </div>
    );
  }

  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden ${className}`}
    >
      <div className="p-6 border-b border-gray-100 bg-gray-50">
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <ShieldCheckIcon className="h-6 w-6 text-indigo-600" />
              Compliance Dashboard
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Track RTB registrations and compliance requirements
            </p>
          </div>
          {/* Landlord Type Badge */}
          {tenancyCount !== null && (
            <div className="flex-shrink-0 ml-4">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                  isSmallLandlord
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-purple-100 text-purple-800'
                }`}
              >
                {isSmallLandlord ? '🏠 Small Landlord' : '🏢 Large Landlord'}
              </span>
            </div>
          )}
        </div>

        {/* Tenancy Count Configuration */}
        <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">
                How many tenancies do you manage?
              </label>
              <p className="text-xs text-gray-500 mt-1">
                {isSmallLandlord && 'Small landlord rules apply (≤3 tenancies)'}
                {isLargeLandlord && 'Large landlord rules apply (≥4 tenancies)'}
                {tenancyCount === null &&
                  'This helps us provide relevant compliance rules'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {editingTenancyCount ? (
                <>
                  <input
                    type="number"
                    min="0"
                    value={tempTenancyCount}
                    onChange={(e) => setTempTenancyCount(e.target.value)}
                    className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="0"
                  />
                  <button
                    onClick={handleSaveTenancyCount}
                    disabled={saving}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setEditingTenancyCount(false)}
                    className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <span className="text-2xl font-bold text-gray-900">
                    {tenancyCount !== null ? tenancyCount : '—'}
                  </span>
                  <button
                    onClick={() => {
                      setEditingTenancyCount(true);
                      setTempTenancyCount(tenancyCount?.toString() || '');
                    }}
                    className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
                  >
                    Edit
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Compliance Rules Section - Re-design */}
      {complianceRules && (
        <div className="p-4 sm:p-6 bg-white shadow-xl rounded-xl border border-indigo-100">
          {/* Card Header (More prominent and distinct) */}
          <div className="flex items-center justify-between pb-4 mb-4 border-b border-indigo-100">
            <h2 className="text-xl sm:text-2xl font-extrabold text-indigo-800 flex items-center gap-3">
              {/* Shield Icon: Larger and more thematic color */}
              <ShieldCheckIcon
                className="h-7 w-7 text-indigo-600"
                aria-hidden="true"
              />
              {/* Dynamic Category Title */}
              Compliance Rules for **{complianceRules.category}**
            </h2>
            {/* Optional: Status badge for the whole category */}
            <span className="inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-700">
              Mandatory
            </span>
          </div>

          {/* Compliance Rules Accordion/Sections */}
          <div className="space-y-3">
            {/* --- RTB Registration --- */}
            {complianceRules.rtbRegistration && (
              <div className="rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden border border-gray-100">
                <button
                  onClick={() => toggleSection('rtbRegistration')}
                  className="w-full px-4 py-3 sm:px-6 flex items-center justify-between bg-white hover:bg-indigo-50 transition-colors"
                >
                  <h4 className="text-lg font-bold text-gray-800 flex items-center gap-3">
                    <span className="text-xl text-indigo-500">📋</span>
                    RTB Registration
                  </h4>
                  <span
                    className={`transform transition-transform duration-300 ${
                      expandedSections.rtbRegistration ? 'rotate-180' : ''
                    } text-gray-400`}
                    aria-hidden="true"
                  >
                    <ChevronDownIcon className="h-5 w-5" />
                  </span>
                </button>

                {expandedSections.rtbRegistration && (
                  <div className="px-4 py-4 sm:px-6 bg-gray-50 border-t border-gray-200 text-sm space-y-3">
                    <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Required:</span>
                        {/* Badge for Required Status */}
                        <span
                          className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${
                            complianceRules.rtbRegistration.required
                              ? 'bg-green-100 text-green-800' // Green for required/positive
                              : 'bg-red-100 text-red-800' // Red for not required/negative
                          }`}
                        >
                          {complianceRules.rtbRegistration.required
                            ? 'Yes'
                            : 'No'}
                        </span>
                      </div>

                      <div>
                        <span className="text-gray-600">Frequency:</span>
                        <p className="font-medium text-gray-900">
                          {complianceRules.rtbRegistration.frequency}
                        </p>
                      </div>

                      <div>
                        <span className="text-gray-600">Deadline:</span>
                        <p className="font-medium text-gray-900">
                          {complianceRules.rtbRegistration.deadline}
                        </p>
                      </div>
                    </div>

                    <a
                      href={complianceRules.rtbRegistration.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center text-sm font-semibold text-indigo-600 hover:text-indigo-700 transition-colors group"
                    >
                      Register on RTB Portal
                      <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* --- Termination Grounds (Simplified for clarity) --- */}
            {complianceRules.terminationGrounds &&
              complianceRules.terminationGrounds.length > 0 && (
                <div className="rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden border border-gray-100">
                  <button
                    onClick={() => toggleSection('terminationGrounds')}
                    className="w-full px-4 py-3 sm:px-6 flex items-center justify-between bg-white hover:bg-indigo-50 transition-colors"
                  >
                    <h4 className="text-lg font-bold text-gray-800 flex items-center gap-3">
                      <span className="text-xl text-indigo-500">⚖️</span>
                      Termination Grounds
                    </h4>
                    <span
                      className={`transform transition-transform duration-300 ${
                        expandedSections.terminationGrounds ? 'rotate-180' : ''
                      } text-gray-400`}
                      aria-hidden="true"
                    >
                      <ChevronDownIcon className="h-5 w-5" />
                    </span>
                  </button>

                  {expandedSections.terminationGrounds && (
                    <div className="px-4 py-4 sm:px-6 border-t border-gray-200 bg-gray-50 text-sm space-y-4">
                      {complianceRules.terminationGrounds.map((ground, idx) => (
                        <div
                          key={idx}
                          // Stronger visual emphasis on each ground
                          className="bg-white p-3 rounded-md border border-red-200 shadow-sm"
                        >
                          <p className="font-semibold text-red-700 text-base mb-1">
                            {ground.ground}
                          </p>
                          <p className="text-gray-700 text-sm">
                            Notice Period:{' '}
                            <strong className="text-gray-900">
                              {ground.noticePeriod} days
                            </strong>
                          </p>
                          {ground.requiresRtb && (
                            <p className="inline-flex items-center text-orange-600 text-xs mt-1 font-medium">
                              <ExclamationTriangleIcon
                                className="h-4 w-4 mr-1"
                                aria-hidden="true"
                              />
                              RTB notification required
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

            {/* --- Rent Review (Simplified for brevity, following the RTB model) --- */}
            {complianceRules.rentReview && (
              <div className="rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden border border-gray-100">
                <button
                  onClick={() => toggleSection('rentReview')}
                  className="w-full px-4 py-3 sm:px-6 flex items-center justify-between bg-white hover:bg-indigo-50 transition-colors"
                >
                  <h4 className="text-lg font-bold text-gray-800 flex items-center gap-3">
                    <span className="text-xl text-indigo-500">💰</span>
                    Rent Review
                  </h4>
                  <span
                    className={`transform transition-transform duration-300 ${
                      expandedSections.rentReview ? 'rotate-180' : ''
                    } text-gray-400`}
                    aria-hidden="true"
                  >
                    <ChevronDownIcon className="h-5 w-5" />
                  </span>
                </button>
                {expandedSections.rentReview && (
                  <div className="px-4 py-4 sm:px-6 bg-gray-50 border-t border-gray-200 text-sm space-y-3">
                    <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Eligible:</span>
                        <span
                          className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${
                            complianceRules.rentReview.eligible
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {complianceRules.rentReview.eligible ? 'Yes' : 'No'}
                        </span>
                      </div>

                      <div>
                        <span className="text-gray-600">Minimum Period:</span>
                        <p className="font-medium text-gray-900">
                          {complianceRules.rentReview.minPeriod} months
                        </p>
                      </div>

                      <div>
                        <span className="text-gray-600">Max Increase:</span>
                        <p className="font-medium text-gray-900">
                          {complianceRules.rentReview.maxIncrease}
                        </p>
                      </div>

                      <div>
                        <span className="text-gray-600">Notice Required:</span>
                        <p className="font-medium text-gray-900">
                          {complianceRules.rentReview.noticeRequired} days
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ... other sections (Deposit Protection, Inspection Rights, Additional Notes) would follow the same card/accordion pattern ... */}
          </div>
        </div>
      )}
      <div className="divide-y divide-gray-100">
        {properties.map((property) => {
          const propertyTenancies = tenancies[property.id] || [];
          const status = getComplianceStatus(property, propertyTenancies);

          if (!status) return null; // Skip properties without active tenancies

          const isCompliant = status.issues.length === 0;

          return (
            <div
              key={property.id}
              className="p-6 hover:bg-gray-50 transition-colors"
            >
              {/* Card Header */}
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-start gap-3">
                  {/* Visual Anchor Icon */}
                  <div className="p-2 bg-indigo-50 rounded-lg hidden sm:block">
                    <HomeIcon className="w-6 h-6 text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 leading-tight">
                      {property.title}
                    </h3>
                    {/* Only show location if strictly different from title to avoid redundancy */}
                    {property.location &&
                      property.location !== property.title && (
                        <p className="text-sm text-gray-500 mt-0.5">
                          {property.location}
                        </p>
                      )}
                  </div>
                </div>

                {/* Consistent Badging: Always show status */}
                <div className="flex-shrink-0 ml-4">
                  {isCompliant ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      <CheckCircleIcon className="h-4 w-4 mr-1.5" />
                      Compliant
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                      <ExclamationTriangleIcon className="h-4 w-4 mr-1.5" />
                      Action Required
                    </span>
                  )}
                </div>
              </div>

              {/* Status & Issues Area */}
              <div className="sm:pl-14">
                {status.issues.length > 0 ? (
                  <div className="space-y-3">
                    {status.issues.map((issue, idx) => (
                      <div
                        key={idx}
                        className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg border ${
                          issue.type === 'error'
                            ? 'bg-red-50 border-red-100'
                            : issue.type === 'warning'
                              ? 'bg-yellow-50 border-yellow-100'
                              : 'bg-blue-50 border-blue-100'
                        }`}
                      >
                        <div className="flex items-start mb-3 sm:mb-0">
                          {issue.type === 'error' && (
                            <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                          )}
                          {issue.type === 'warning' && (
                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                          )}
                          {issue.type === 'info' && (
                            <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                          )}

                          <div>
                            <p
                              className={`text-sm font-bold ${
                                issue.type === 'error'
                                  ? 'text-red-900'
                                  : issue.type === 'warning'
                                    ? 'text-yellow-900'
                                    : 'text-blue-900'
                              }`}
                            >
                              {issue.message}
                            </p>
                            {issue.detail && (
                              <p className="text-xs text-gray-600 mt-1">
                                {issue.detail}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Updated Action Button Logic */}
                        {issue.action && (
                          <div className="flex-shrink-0 sm:ml-4">
                            {issue.url ? (
                              <a
                                href={issue.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`no-underline inline-flex items-center px-4 py-2 text-sm font-medium rounded-md shadow-sm transition-colors ${
                                  issue.type === 'error'
                                    ? 'bg-white text-red-700 border border-red-300 hover:bg-red-50'
                                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {issue.action}
                                <ArrowTopRightOnSquareIcon className="ml-2 h-4 w-4" />
                              </a>
                            ) : (
                              <button
                                className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                                  issue.type === 'error'
                                    ? 'bg-red-600 text-white hover:bg-red-700 shadow-sm'
                                    : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                                }`}
                              >
                                {issue.action}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                    <div className="text-sm text-green-800 flex items-center font-medium mb-2">
                      <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                      All compliance checks passed
                    </div>
                    {status.checks.map((check, idx) => (
                      <div
                        key={idx}
                        className="text-xs text-green-700 ml-7 flex items-center"
                      >
                        <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-2"></span>
                        {check}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {properties.length === 0 && !loading && (
          <div className="p-12 text-center">
            <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No managed properties
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by adding a property to your portfolio.
            </p>
          </div>
        )}
      </div>

      {/* Modal */}
      {modal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full">
            {/* Modal Header */}
            <div
              className={`px-6 py-4 border-b ${
                modal.type === 'error'
                  ? 'bg-red-50 border-red-200'
                  : modal.type === 'warning'
                    ? 'bg-yellow-50 border-yellow-200'
                    : modal.type === 'success'
                      ? 'bg-green-50 border-green-200'
                      : 'bg-blue-50 border-blue-200'
              }`}
            >
              <div className="flex items-center gap-3">
                {modal.type === 'error' && (
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                )}
                {modal.type === 'warning' && (
                  <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
                )}
                {modal.type === 'success' && (
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                )}
                {modal.type === 'info' && (
                  <InformationCircleIcon className="h-6 w-6 text-blue-600" />
                )}
                <h2
                  className={`text-lg font-semibold ${
                    modal.type === 'error'
                      ? 'text-red-900'
                      : modal.type === 'warning'
                        ? 'text-yellow-900'
                        : modal.type === 'success'
                          ? 'text-green-900'
                          : 'text-blue-900'
                  }`}
                >
                  {modal.title}
                </h2>
              </div>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4">
              <p className="text-gray-700 text-sm">{modal.message}</p>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setModal({ ...modal, isOpen: false })}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  modal.type === 'error'
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : modal.type === 'warning'
                      ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                      : modal.type === 'success'
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceDashboard;
